import type { ProceduralTimelineEvent } from '../../../../backend/src/matter/procedural-timeline/procedural-timeline-event.entity';

/**
 * Outlook 365 deep-link builder: opens a pre-filled compose view. Outlook
 * generates its own event id on save, so re-clicking creates a new event each
 * time. The backend `.eml` flow remains the canonical "update on re-export" path.
 */

const DEFAULT_DURATION_MS = 60 * 60 * 1000;
const OUTLOOK_COMPOSE_URL = 'https://outlook.office.com/calendar/0/deeplink/compose';
/**
 * Outlook's web compose endpoint silently truncates the body and attendee list
 * past ~2k characters. Microsoft doesn't document the limit; this is empirical.
 */
export const OUTLOOK_DEEP_LINK_MAX_URL_LENGTH = 1900;

interface DeepLinkInputs {
  event: ProceduralTimelineEvent;
  attendeeEmails: string[];
  wexlerUrl: string;
  matterName: string;
  creatorEmail: string;
}

export function buildOutlookCalendarUrl({
  event,
  attendeeEmails,
  wexlerUrl,
  matterName,
  creatorEmail,
}: DeepLinkInputs): string {
  if (event.startDateTime === null) {
    throw new Error('Cannot build calendar link for an event with no start date');
  }
  const start = new Date(event.startDateTime);
  const end =
    event.endDateTime !== null ? new Date(event.endDateTime) : new Date(start.getTime() + DEFAULT_DURATION_MS);

  const params = new URLSearchParams({
    path: '/calendar/action/compose',
    rru: 'addevent',
    subject: `${event.name} - ${matterName}`,
    // Outlook's deep-link has no timezone param, so emit a fully-qualified ISO
    // stamp with the event timezone's actual offset. The event then lands at
    // the right instant regardless of the viewing user's calendar tz.
    startdt: formatIsoWithOffset(start, event.timeZone),
    enddt: formatIsoWithOffset(end, event.timeZone),
    body: buildBody(event, wexlerUrl, matterName, creatorEmail),
    allday: 'false',
  });
  if (attendeeEmails.length > 0) {
    params.set('to', attendeeEmails.join(','));
  }
  return `${OUTLOOK_COMPOSE_URL}?${params.toString()}`;
}

function buildBody(
  event: ProceduralTimelineEvent,
  wexlerUrl: string,
  matterName: string,
  creatorEmail: string,
): string {
  // Outlook's deep-link `body` param renders as HTML — using <a> here gives a
  // clickable "View the event in Wexler" link instead of a naked URL.
  const wexlerLink = `<a href="${escapeHtml(wexlerUrl)}">View the event in Wexler</a>`;
  const lines: string[] = [
    `This is a calendar holder for the procedural event <b>${escapeHtml(event.name)}</b> for Matter <b>${escapeHtml(matterName)}</b>.`,
  ];
  const description = event.description?.trim();
  if (description !== undefined && description !== '') {
    lines.push('', '<b>Description:</b>', escapeHtml(description));
  }
  lines.push('', `This event was created by ${escapeHtml(creatorEmail)}. ${wexlerLink}`);
  return lines.join('<br>');
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

function formatIsoWithOffset(date: Date, tzId: string | null): string {
  const naive =
    `${date.getUTCFullYear()}-${pad2(date.getUTCMonth() + 1)}-${pad2(date.getUTCDate())}` +
    `T${pad2(date.getUTCHours())}:${pad2(date.getUTCMinutes())}:${pad2(date.getUTCSeconds())}`;
  if (tzId === null || tzId.trim() === '') {
    return `${naive}Z`;
  }
  return `${naive}${computeOffset(date, tzId)}`;
}

/** `+HH:MM` / `-HH:MM` for the wall-clock instant in `tzId`. Uses `Intl.DateTimeFormat` to handle DST. */
function computeOffset(date: Date, tzId: string): string {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', { timeZone: tzId, timeZoneName: 'longOffset' });
    const parts = formatter.formatToParts(date);
    const tzPart = parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
    const match = /GMT([+-])(\d{1,2})(?::?(\d{2}))?/.exec(tzPart);
    if (match === null) return 'Z';
    const sign = match[1];
    const hours = pad2(parseInt(match[2], 10));
    const minutes = match[3] !== undefined ? match[3] : '00';
    return `${sign}${hours}:${minutes}`;
  } catch {
    return 'Z';
  }
}
